/** ELPM Studio projects API (private studio — the session cookie authenticates every call). */
import axios from "axios";
import { API } from "@/lib/creatorApi";
import { chunkedUpload } from "@/lib/chunkedUpload";

const http = axios.create({ baseURL: `${API}/projects`, withCredentials: true });
const data = (p) => p.then((r) => r.data);

let catalogPromise = null;
export function getCatalog() {
  if (!catalogPromise) catalogPromise = data(http.get("/catalog")).catch((e) => { catalogPromise = null; throw e; });
  return catalogPromise;
}

export const listVoices = () => data(http.get("/voices"));
export const listProjects = (params = {}) => data(http.get("", { params }));
export const listFolders = () => data(http.get("/folders"));
export const getProject = (id) => data(http.get(`/${id}`));
export const createProject = (body) => data(http.post("", body));
export const updateProject = (id, body) => data(http.patch(`/${id}`, body));
export const duplicateProject = (id) => data(http.post(`/${id}/duplicate`));
export const deleteProject = (id, { hard = false } = {}) => data(http.delete(`/${id}`, { params: { hard } }));
export const restoreProject = (id) => data(http.post(`/${id}/restore`));
export const generateProject = (id) => data(http.post(`/${id}/generate`));

/** Your own song for "Add a band" / "Add singing": 5 MB pieces (no size limit from the proxy), first 8 minutes kept. */
export const uploadSourceAudio = (id, file, onProgress) => chunkedUpload(http, {
  start: `/${id}/source`, chunk: (uid, i) => `/${id}/source/${uid}/chunks/${i}`, finish: (uid) => `/${id}/source/${uid}/finish`,
}, file, { filename: file.name, size: file.size }, {}, onProgress);
export const removeSourceAudio = (id) => data(http.delete(`/${id}/source`));
export const saveSourceTempo = (id, tempo) => data(http.put(`/${id}/source/tempo`, tempo));
export const sourceAudioUrl = (id, src) => `${API}/projects/${id}/source/audio?v=${encodeURIComponent(src?.uploaded_at || "")}`;
export const removeVersion = (id, versionId) => data(http.delete(`/${id}/versions/${versionId}`));
export const checkVersionLyrics = (id, versionId) => data(http.post(`/${id}/versions/${versionId}/check_lyrics`));
export const startBacking = (id, versionId) => data(http.post(`/${id}/versions/${versionId}/backing`));
export const getBacking = (id, versionId) => data(http.get(`/${id}/versions/${versionId}/backing`));

/** Kept take → server mixes it with the backing and adds a "My vocal" version. */
export function uploadProjectTake(id, { blob, versionId, trimHeadSec, backingMode, singerProfileId }) {
  const fd = new FormData();
  const ext = (blob.type || "").includes("mp4") ? "m4a" : (blob.type || "").includes("wav") ? "wav" : "webm";
  fd.append("file", blob, `take.${ext}`);
  fd.append("version_id", versionId);
  fd.append("trim_head_sec", String(Math.min(5, Math.max(0, trimHeadSec || 0)).toFixed(3)));
  fd.append("backing_mode", backingMode);
  if (singerProfileId) fd.append("singer_profile_id", singerProfileId);
  return data(http.post(`/${id}/takes`, fd));
}

const studio = axios.create({ baseURL: `${API}/studio`, withCredentials: true });
export const listVoiceProfiles = () => data(studio.get("/voice_profiles"));
export const createVoiceProfile = (body) => data(studio.post("/voice_profiles", body));
export const updateVoiceProfile = (pid, body) => data(studio.patch(`/voice_profiles/${pid}`, body));
export const makeMainVoiceProfile = (pid) => data(studio.post(`/voice_profiles/${pid}/main`));
export const deleteVoiceProfile = (pid) => data(studio.delete(`/voice_profiles/${pid}`));
export const getVoicePreview = (pid) => data(studio.get(`/voice_profiles/${pid}/preview`));
export const makeVoicePreview = (pid) => data(studio.post(`/voice_profiles/${pid}/preview`));
export const VOICE_PREVIEW_BUSY = ["starting", "band", "singing", "mixing"];

export const shortVoiceName = (name) => (name || "").split(" — ")[0];

/** A vocal recorded somewhere else (another studio, a phone) becomes a take on that version. */
export function uploadTakeFile(id, { file, versionId, singerProfileId }) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("version_id", versionId);
  if (singerProfileId) fd.append("singer_profile_id", singerProfileId);
  return data(http.post(`/${id}/takes/upload`, fd));
}
export const replaceVocal = (id, body) => data(http.post(`/${id}/replace_vocal`, body));
export const deleteTake = (id, takeId) => data(http.delete(`/${id}/takes/${takeId}`));
export const listVocalJobs = (id, versionId) => data(http.get(`/${id}/vocal_jobs`, { params: { version_id: versionId } }));
export const getVocalJob = (id, jobId) => data(http.get(`/${id}/vocal_jobs/${jobId}`));

export const VOCAL_JOB_PHASES = {
  starting: "Getting ready",
  lining_up: "Lining up your take",
  cleaning: "Cleaning the background noise",
  tuning: "Cleaning up & tuning your notes",
  mixing: "Mixing your voice with the music",
  saving: "Saving the new version",
  done: "Done",
  error: "Stopped",
};

export const getSongStems = (songId) => data(studio.get(`/songs/${songId}/stems`));
export const splitSongStems = (songId, parts) => data(studio.post(`/songs/${songId}/stems`, { parts }));
export const stemsZipUrl = (setId, format) => `${API}/studio/stems/${setId}/all.zip?format=${format}`;

export const STEM_PHASES = {
  starting: "Getting ready",
  separating: "Separating the parts",
  saving: "Saving full-quality copies",
  done: "Ready",
  error: "Stopped",
};

export function fmtBytes(n) {
  if (!n) return "";
  return n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e8 ? 0 : 1)} MB` : `${Math.round(n / 1e3)} KB`;
}

/** Fields the Create Music form edits (everything else on a project is server-managed). */
export const EDITABLE_FIELDS = ["title", "artist", "lyrics", "lyrics_lock", "genre_key", "custom_style", "bpm", "key",
  "instruments", "structure", "duration_sec", "vocal_style", "vocal", "arrangement", "engine", "folder", "start_mode"];

export function pickEditable(p) {
  const out = {};
  for (const k of EDITABLE_FIELDS) if (p && k in p) out[k] = p[k];
  return out;
}

export const UPLOAD_COMPOSING = { add_band: "Adding a full band under your singing", add_singing: "Adding singing over your music", grow_loop: "Growing your loop into a full song" };

export const GENERATION_PHASES = {
  composing: "Composing music & vocals",
  checking_lyrics: "Isolating the vocal & checking your lyrics",
  converting_voice: "Re-singing in your recorded voice",
  done: "Done",
  error: "Stopped",
};

export const BACKING_PHASES = {
  starting: "Getting ready",
  separating: "Separating the vocals from the music",
  rendering: "Building your backing track",
  finding_parts: "Finding where each part is sung",
  done: "Ready",
  error: "Stopped",
};

const LEGACY_STYLE_TO_GENRE = {
  "gospel-reggae": "jamaican-gospel-reggae",
  "gospel-rnb": "rnb-gospel-slow-jam",
  worship: "contemporary-gospel-worship",
  "contemporary-gospel": "contemporary-gospel-worship",
};

/** Map an old Creator draft / homepage `?style=` key onto the studio catalog. */
export function genreFromLegacyStyle(styleKey, catalog) {
  if (!styleKey) return null;
  const keys = new Set([...(catalog?.gospel_genres || []), ...(catalog?.more_genres || [])].map((g) => g.key));
  const mapped = LEGACY_STYLE_TO_GENRE[styleKey] || styleKey;
  return keys.has(mapped) ? mapped : null;
}

export function fmtClock(secs) {
  if (!secs && secs !== 0) return "";
  const s = Math.max(0, Math.round(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function fmtAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)} d ago`;
  return new Date(t).toLocaleDateString();
}

/** Lyrics-check verdict → label + tone for the UI. */
export function lyricsVerdict(check) {
  if (!check) return null;
  if (check.status === "pending" || check.status === "running") return { tone: "pending", label: "Checking lyrics…" };
  if (check.status === "error") return { tone: "error", label: "Lyrics check failed" };
  const pct = check.score != null ? Math.round(check.score * 100) : null;
  switch (check.verdict) {
    case "match": return { tone: "good", label: `Lyrics match · ${pct}% heard`, pct };
    case "minor": return { tone: "warn", label: `Some words may differ · ${pct}% heard`, pct };
    case "changed": return { tone: "bad", label: `Lyrics changed · only ${pct}% heard`, pct };
    case "unclear": return { tone: "warn", label: "Vocals too unclear to check", pct };
    default: return null;
  }
}
